import { apiGet, apiPost } from '@forwardflow/services/Axios/configs'
import { ResponseSuccess } from '@forwardflow/types'
import { IAuth } from '@forwardflow/types/auth'

// Get access token after sso redirect
export default class AuthService {
   static apiGenerateAccessToken = async (token: string | null) => {
      const data = await apiGet<ResponseSuccess<IAuth>>({
         apiPath: `/auth/generate-access-token?token=${token}`,
         includeXOrg: false,
         // The response SETS `refresh_token`. Without this the cookie is
         // discarded cross-origin — the axios instance opts in, but every call
         // passes an explicit `withCredentials` that overrides it.
         withCredentials: true,
      })
      return data.data
   }
   static apiValidateEmail = async (token: string | null) => {
      const data = await apiPost<ResponseSuccess<IAuth>>({
         apiPath: `/auth/verify-email?token=${token}&redirectUrl=${window.location.origin}`,
         includeXOrg: false,
         // Also sets `refresh_token` — see above.
         withCredentials: true,
      })
      return data.data
   }
   static apiSignup = async (data: any, url: string | null) => {
      const response = await apiPost<ResponseSuccess<IAuth>>({
         apiPath: `/auth/sign-up?redirectUrl=${url}`,
         data,
         includeXOrg: false,
         // Sign-up sets `refresh_token` exactly as login does, so it needs the
         // same opt-in. A new account that goes straight into the MCP handoff
         // would otherwise arrive with no session.
         withCredentials: true,
      })
      return response.data
   }
   static apiSignin = async (data: any) => {
      const response = await apiPost<ResponseSuccess<IAuth>>({
         apiPath: `/auth/login`,
         data,
         includeXOrg: false,
         // THE COOKIE THIS WHOLE FLOW DEPENDS ON. `refresh_token` is what
         // Internal BE's MCP handoff authenticates with, and without this the
         // browser drops it on a cross-origin response — so a successful login
         // still arrives at `/auth/mcp/start` with no session.
         withCredentials: true,
      })
      return response.data
   }
   static apiForgotPassword = async (url: string | null, email: string) => {
      const data = await apiPost<ResponseSuccess<any>>({
         apiPath: `/auth/forgot-password?redirectUrl=${url}`,
         data: {
            email: email,
         },
         includeXOrg: false,
      })
      return data.data
   }
   static apiResetPassword = async (
      token: string | null,
      password: string,
      redirectUrl: string
   ) => {
      const data = await apiPost<ResponseSuccess<IAuth>>({
         apiPath: `/auth/new-password?token=${token}&redirectUrl=${redirectUrl}`,
         data: {
            // token: token,
            newPassword: password,
         },
         includeXOrg: false,
         // Also returns an authenticated session with a `refresh_token`.
         withCredentials: true,
      })
      return data.data
   }
   static apiResendVerificationMail = async (url: string | null) => {
      const data = await apiPost<ResponseSuccess<{
         success?: boolean
         message?: string
         timeRemaining?: number
      }>>({
         apiPath: `/auth/resend-verification-email?redirectUrl=${url}`,
         includeXOrg: false,
      })
      return data.data
   }

   static apiRefreshToken = async () => {
      const data = await apiPost<ResponseSuccess<IAuth>>({
         apiPath: `/auth/refresh-token`,
         includeXOrg: false,
         withCredentials: true
      })
      return data.data
   }

   static apiLogout = async () => {
      const data = await apiPost<ResponseSuccess<null>>({
         apiPath: `/auth/logout`,
         includeXOrg: false,
         // THE CLEAR NEEDS CREDENTIALS AS MUCH AS THE SET DOES.
         //
         // Without this the browser discards the response's expiry headers and
         // the refresh cookie survives logout. That was survivable while only
         // the dashboard read it — the access token was gone, so the session
         // looked ended. It stopped being survivable when the MCP handoff
         // began authenticating from the refresh cookie alone: the previous
         // user's cookie can then complete a connection started by whoever is
         // at the keyboard now.
         withCredentials: true,
      })
      return data.data
   }
}
